import { z } from "zod";
import { CLIENT_PLATFORMS } from "./auth";

/**
 * Error reports from the apps (`POST /api/client-errors`). An app sends one when its own code fails — an uncaught
 * exception, a crashed screen, a broken query — so the failure reaches the server's error log next to the API's own.
 * Expected outcomes are never reported: an API refusal (the API logs its failures itself), no connection, a cancel.
 *
 * The wire still accepts older clients' free-text fields. New clients send only finite error classifications and
 * allowlisted metadata; the API applies the same policy before logging older reports.
 */
export const CLIENT_ERROR_KINDS = [
  /** A thrown error nothing caught (window `error`, React Native's global handler). */
  "uncaught",
  /** A rejected promise nothing handled. */
  "unhandledRejection",
  /** A screen failed to render and its error boundary took over. */
  "render",
  /** A query or mutation failed with something other than an API answer or a lost connection. */
  "query",
  "mutation",
  /** The app is about to close because of the error (React Native `isFatal`). */
  "fatal",
] as const;
export type ClientErrorKind = (typeof CLIENT_ERROR_KINDS)[number];

export const CLIENT_ERROR_LIMITS = {
  name: 120,
  message: 1_000,
  stack: 8_000,
  route: 200,
  appVersion: 40,
  bundleLine: 100_000,
  bundleColumn: 9_999_999,
  contextKeys: 12,
  contextKey: 40,
  contextValue: 200,
  /** Per app run, so a render loop cannot flood the log. */
  reportsPerSession: 20,
  /** Request body ceiling at the API (`reportBodyLimit`). */
  bodyBytes: 16 * 1024,
} as const;

const L = CLIENT_ERROR_LIMITS;
/** Over-long text is cut, not refused: a report that is too long is still worth having. */
const clipped = (max: number) => z.string().transform((s) => s.slice(0, max));

export const clientErrorReportSchema = z.object({
  platform: z.enum(CLIENT_PLATFORMS),
  appVersion: z.string().trim().min(1).max(L.appVersion),
  kind: z.enum(CLIENT_ERROR_KINDS),
  name: clipped(L.name).default("Error"),
  message: clipped(L.message).default(""),
  stack: clipped(L.stack).optional(),
  route: clipped(L.route).optional(),
  context: z
    .record(z.string().max(L.contextKey), z.union([clipped(L.contextValue), z.number(), z.boolean()]))
    .refine((o) => Object.keys(o).length <= L.contextKeys, { message: `At most ${L.contextKeys} context entries.` })
    .optional(),
  /** When the error happened on the device (its clock; the server stamps its own time on the log line). */
  at: z.iso.datetime({ offset: true }).optional(),
});
export type ClientErrorReport = z.input<typeof clientErrorReportSchema>;
export type ClientErrorReportInput = z.output<typeof clientErrorReportSchema>;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Removes what must never reach a log from free text: e-mail addresses, credentials in headers, URL parameters that
 * carry secrets, JWTs and long random strings (session, invite, RSVP, share and calendar tokens are 32+ characters).
 * UUIDs stay: they are the ids of rows and weddings, not secrets, and they are what makes a log line traceable.
 */
export function scrubText(text: string): string {
  return text
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, "[email]")
    .replace(/\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]+/gi, "$1 [redacted]")
    .replace(/\b(cookie|set-cookie|authorization)(["']?\s*[:=]\s*["']?)[^"'\n]+/gi, "$1$2[redacted]")
    .replace(/\beyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}/g, "[jwt]")
    .replace(/([?&;#]|\b)(token|code|password|passcode|secret|key|signature|sig|auth|session_token|_ptxn)=([^&\s"'#]+)/gi, "$1$2=[redacted]")
    .replace(/[A-Za-z0-9_-]{20,}/g, (m) => (UUID.test(m) || !/\d/.test(m) || !/[A-Za-z]/.test(m) ? m : "[redacted]"));
}

const ERROR_DESCRIPTIONS: Record<string, string> = {
  Error: "Unexpected operation failure",
  TypeError: "Unexpected value type or unavailable dependency",
  RangeError: "Value outside supported range",
  ReferenceError: "Unavailable program reference",
  SyntaxError: "Invalid encoded data or program syntax",
  URIError: "Invalid URI encoding",
  EvalError: "Program evaluation failed",
  AggregateError: "Multiple operations failed",
  AbortError: "Operation aborted",
  TimeoutError: "Operation timed out",
  AppError: "Server operation failed",
  DrizzleQueryError: "Database operation failed",
};

/** Never includes exception text, stacks, SQL, thrown objects or their string representations. */
export function safeErrorDescription(error: unknown): { name: string; message: string } {
  const name = error instanceof Error ? error.name : "";
  if (Object.hasOwn(ERROR_DESCRIPTIONS, name)) return { name, message: ERROR_DESCRIPTIONS[name]! };
  return error instanceof Error
    ? { name: "Error", message: ERROR_DESCRIPTIONS.Error! }
    : { name: "NonError", message: "Non-error value thrown" };
}

export interface DiagnosticSource {
  bundleLine: number;
  bundleColumn: number;
}

/**
 * Only positions inside the shipped first-party web or Android bundle are useful without sending arbitrary stack
 * text. In particular, never extract source paths, URLs, function names or exception messages from a frame.
 */
export function safeDiagnosticSource(stack: string | undefined, platform: string): DiagnosticSource | undefined {
  if (!stack || (platform !== "web" && platform !== "android")) return undefined;
  for (const frame of stack.split("\n", 12)) {
    if (frame.length > 1_024) continue;
    const match = /(?:\(|@|^| at )(?:address at )?([^\s()@]+):([1-9]\d{0,5}):([1-9]\d{0,6})\)?\s*$/.exec(frame.trim());
    if (!match) continue;
    const file = match[1]!;
    if (platform === "web"
      ? !/^https:\/\/bridewell\.io\/assets\/index-[A-Za-z0-9_-]{6,12}\.js$/.test(file)
      : file !== "index.android.bundle") continue;
    const bundleLine = Number(match[2]);
    const bundleColumn = Number(match[3]);
    if (bundleLine <= L.bundleLine && bundleColumn <= L.bundleColumn) return { bundleLine, bundleColumn };
  }
  return undefined;
}

/** Deduplicate by safe failure facts, not raw messages or stack frames (which can change for every user). */
export function errorFingerprint(e: { name: string; source?: DiagnosticSource }): string {
  const name = e.name === "NonError" || Object.hasOwn(ERROR_DESCRIPTIONS, e.name) ? e.name : "Error";
  const source = safeDiagnosticContext(e.source ? { ...e.source } : undefined);
  return JSON.stringify([name, source.bundleLine ?? 0, source.bundleColumn ?? 0]);
}

const ROUTE_WORDS = new Set([
  "app", "(app)", "(tabs)", "w", "weddings", "guests", "checklist", "budget", "vendors", "timeline", "shopping",
  "notes", "inspiration", "registry", "seating", "activity", "dashboard", "insights", "payments", "members",
  "settings", "account", "security", "billing", "notifications", "planner", "templates", "more", "edit", "new",
  "login", "signup", "forgot-password", "reset-password", "verify-email", "join", "rsvp", "share", "contact",
  "auth", "app-return", "privacy", "terms", "cookies", "legal", "index", "welcome", "onboarding",
]);
const ROUTE_PARAMS = new Set(["$weddingId", "$id", "$token", "$module", ":weddingId", ":id", ":token", ":module", "[weddingId]", "[id]", "[token]", "[module]"]);

/** Unknown segments are omitted, not scrubbed: a short name or token can look exactly like a route word. */
export function safeDiagnosticRoute(route: string): string {
  if (route.length > CLIENT_ERROR_LIMITS.route || !route.startsWith("/") || /[?#\\]/.test(route)) return "/:unknown";
  const segments = route.split("/");
  return segments.every((part) => !part || ROUTE_WORDS.has(part) || ROUTE_PARAMS.has(part)) ? route : "/:unknown";
}

export function safeDiagnosticVersion(version: string): string {
  return /^(?:[0-9a-f]{7,40}|dev|unknown|\d{1,3}\.\d{1,3}\.\d{1,3}(?: \(\d{1,8}\))?)$/i.test(version) ? version : "unknown";
}

/** Facts whose values cannot carry user text; additional facts require an explicit privacy review here. */
export function safeDiagnosticContext(context: Record<string, unknown> | undefined): Record<string, number | boolean> {
  const out: Record<string, number | boolean> = {};
  for (const key of ["standalone", "online"]) if (typeof context?.[key] === "boolean") out[key] = context[key];
  if (typeof context?.androidApi === "number" && Number.isInteger(context.androidApi) && context.androidApi >= 1 && context.androidApi <= 100) out.androidApi = context.androidApi;
  if (typeof context?.bundleLine === "number" && Number.isInteger(context.bundleLine)
    && context.bundleLine >= 1 && context.bundleLine <= L.bundleLine
    && typeof context.bundleColumn === "number" && Number.isInteger(context.bundleColumn)
    && context.bundleColumn >= 1 && context.bundleColumn <= L.bundleColumn) {
    out.bundleLine = context.bundleLine;
    out.bundleColumn = context.bundleColumn;
  }
  return out;
}
